import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { Camera, PackageX, Pencil, Plus, Trash2 } from 'lucide-react';
import { ComboBox, type OpcaoCombo } from '../../components/ComboBox';
import { ModalCadastroRapido } from '../../components/ModalCadastroRapido';
import { api } from '../../lib/api';
import { useDialog } from '../../components/DialogProvider';
import { useAuth } from '../../lib/auth';
import { FotoProduto } from '../../components/FotoProduto';
import { Modal } from '../../components/Modal';
import { formatDataHoraSP } from '@fiodecobre/shared';
import { BuscaProdutoEstoque, type ProdutoBuscado } from './BuscaProdutoEstoque';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { LightboxImagem, type ImagemLightbox } from '../atendimento/componentes/LightboxImagem';

// VENDA PERDIDA ([DA-868]) — Lancamentos > Venda Perdida, ultimo item do menu (pedido dele).
//
// *"Uma lista onde o usuario possa alimentar, informando venda perdida do produto (e quantidade)."*
// Todo perfil registra. O servidor grava sozinho o que o SISTEMA dizia ter no instante — e' isso que
// faz a venda perdida servir tambem de afericao do estoque (ver o Premio do Estoquista).
//
// ⚠️ A tela nao mostra o estoque do sistema ANTES do registro: quem registra diz o que viu na
// prateleira, e o numero do sistema aparece so na lista, depois.
//
// Tres origens ([DA-872], pedido dele 31/10/2026): produto do cadastro, Novo Produto (NP00035) e
// produto SEM cadastro (a descricao escrita na hora, com foto opcional). Os dois ultimos nao tem
// estoque no sistema — "Sistema dizia" fica em branco e eles nao pesam no Premio do Estoquista.
// Admin e gerente EDITAM o registro (p.6) no modal, com a mesma peca de escolher a origem.

type Tipo = 'produto' | 'novo_produto' | 'sem_cadastro';
type Item = {
  id: number; criado_em: string; quantidade: string; observacao: string | null; estoque_no_registro: string | null;
  usuario: string | null; tipo: Tipo; produto_id: number | null; novo_produto_id: number | null;
  codigo_livre: string | null; marca_id: number | null; marca: string | null;
  codigo_interno: string | null; descricao: string; unidade: string; estoque_atual: string | null; foto_url: string | null;
};
type Resumo = {
  chave: string; tipo: Tipo; codigo_interno: string | null; descricao: string; unidade: string;
  registros: number; quantidade: string; estoque_atual: string | null; foto_url: string | null;
};

// O que o operador escolheu como "o produto" — uma das tres origens, mais a foto do sem cadastro.
type Origem = {
  produto: ProdutoBuscado | null;
  /** Texto do produto SEM cadastro. null = nao e' esse caso. */
  semCadastro: string | null;
  fotoNova: string | null; // dataURL ainda nao enviado
  fotoMime: string;
  fotoAtual: string | null; // edicao: a que ja esta gravada
  fotoApagar: boolean; // edicao: tirar a gravada
  /** Sem cadastro ([DA-872] p.8-9): o codigo VP##### que o BANCO gerou (so leitura; vazio = registro
   *  novo) e a marca ESCOLHIDA do cadastro de marcas. */
  codigoLivre: string;
  marcaId: number | null;
  marcaNome?: string | null; // edicao: o nome gravado, se a marca nao vier na lista
  /** Edicao: o produto veio do registro, sem as prateleiras (a lista nao as traz). */
  semLugares?: boolean;
};
const ORIGEM_VAZIA: Origem = {
  produto: null, semCadastro: null, fotoNova: null, fotoMime: 'image/png', fotoAtual: null, fotoApagar: false,
  codigoLivre: '', marcaId: null,
};

function origemEscolhida(o: Origem): boolean {
  return !!o.produto || o.semCadastro !== null;
}

function payloadOrigem(o: Origem) {
  if (o.produto) return o.produto.tipo === 'novo_produto' ? { novo_produto_id: o.produto.id } : { produto_id: o.produto.id };
  return {
    descricao_livre: (o.semCadastro ?? '').trim(),
    marca_id: o.marcaId,
    foto: o.fotoNova ? { base64: o.fotoNova, mime: o.fotoMime } : null,
    foto_apagar: !o.fotoNova && o.fotoApagar,
  };
}

const MIMES_FOTO = ['image/jpeg', 'image/png', 'image/webp'];

// Clique amplia (pedido dele, 31/10/2026) — o mesmo LightboxImagem do Orcamento.
function CelulaFoto({ url, legenda, onAmpliar }: { url: string | null; legenda: string; onAmpliar: (f: ImagemLightbox) => void }) {
  return url
    ? <FotoProduto url={url} tam={36} style={{ cursor: 'zoom-in' }} title="Clique para ampliar a foto"
        onClick={() => onAmpliar({ url, legenda })} />
    : <div className="produto-thumb-vazio" style={{ width: 36, height: 36 }} />;
}

// Origem que nao e' do cadastro se diz na propria linha — senao "sem codigo" parece defeito.
function SeloOrigem({ tipo }: { tipo: Tipo }) {
  if (tipo === 'produto') return null;
  return <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>{tipo === 'novo_produto' ? '(Novos Produtos)' : '(sem cadastro)'}</span>;
}

/**
 * Escolher o produto: busca (cadastro + Novos Produtos + "sem cadastro") ou o escolhido com Trocar.
 * No sem cadastro, a area de foto do Novos Produtos (Ctrl+V, arrastar, clicar). `colarAtivo` existe
 * porque a pagina e o modal de edicao tem cada um a sua — so a que esta na frente ouve o Ctrl+V.
 */
function CampoOrigem({ valor, onChange, colarAtivo, onEscolheu, comTrocar = false }: {
  valor: Origem;
  onChange: Dispatch<SetStateAction<Origem>>;
  colarAtivo: boolean;
  onEscolheu?: () => void;
  /** So o modal de edicao: na pagina quem desiste e' o Cancelar do rodape (pedido dele, 31/10/2026 —
   *  um botao so pra desistir, operador com pouca pratica). */
  comTrocar?: boolean;
}) {
  const dialog = useDialog();
  const dialogRef = useRef(dialog);
  useEffect(() => { dialogRef.current = dialog; });
  const arquivoRef = useRef<HTMLInputElement>(null);

  // Marca do sem cadastro: o combo da casa sobre o cadastro de marcas + `+` pra cadastrar na hora
  // (mesmo desenho do Novos Produtos) — nunca digitada, senao fabrica sinonimo ([DA-604]).
  const [marcas, setMarcas] = useState<{ id: number; nome: string }[]>([]);
  const [novaMarca, setNovaMarca] = useState(false);
  useEffect(() => { api.get<{ id: number; nome: string }[]>('/api/marcas').then(setMarcas).catch(() => {}); }, []);
  const opcoesMarca: OpcaoCombo[] = marcas.map((m) => ({ value: m.id, label: m.nome }));
  // A gravada entra na lista mesmo se nao vier nela — senao o combo abre vazio e salvar apaga o dado.
  if (valor.marcaId && !marcas.some((m) => m.id === valor.marcaId)) {
    opcoesMarca.unshift({ value: valor.marcaId, label: valor.marcaNome ?? `Marca ${valor.marcaId}` });
  }

  const aceitarFoto = useCallback((f: File | null | undefined) => {
    if (!f) return;
    if (!MIMES_FOTO.includes(f.type)) { dialogRef.current.toast('So JPG, PNG ou WebP'); return; }
    if (f.size > 8 * 1024 * 1024) { dialogRef.current.toast('Imagem maior que 8MB'); return; }
    const r = new FileReader();
    r.onload = () => onChange((o) => ({ ...o, fotoNova: String(r.result), fotoMime: f.type, fotoApagar: false }));
    r.readAsDataURL(f);
  }, [onChange]);

  const emSemCadastro = valor.semCadastro !== null;
  useEffect(() => {
    if (!colarAtivo || !emSemCadastro) return;
    function onPaste(e: ClipboardEvent) {
      for (const it of e.clipboardData?.items ?? []) {
        if (it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) { e.preventDefault(); aceitarFoto(f); return; }
        }
      }
    }
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [colarAtivo, emSemCadastro, aceitarFoto]);

  // Trocar: a foto gravada sai junto (o servidor ja a tira de quem deixa de ser sem cadastro).
  const trocar = () => onChange((o) => ({ ...ORIGEM_VAZIA, fotoAtual: o.fotoAtual, fotoApagar: !!o.fotoAtual }));

  if (emSemCadastro) {
    const fotoMostrando = valor.fotoNova ?? (valor.fotoApagar ? null : valor.fotoAtual);
    // Layout aprovado no esboco de 31/10/2026: quadrado de foto com borda forte e a palavra FOTO
    // dentro (pedido dele — "o campo da foto precisa ter bordas mais visiveis"), campos maiores.
    return (
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <input ref={arquivoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
          onChange={(e) => { aceitarFoto(e.target.files?.[0]); e.target.value = ''; }} />
        <div style={{ position: 'relative', width: 140, height: 140, flex: '0 0 auto' }}>
          <button
            type="button"
            onClick={() => arquivoRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); aceitarFoto(e.dataTransfer?.files?.[0]); }}
            className="rounded-lg hover:bg-black/5 flex flex-col items-center justify-center gap-1"
            style={{
              width: '100%', height: '100%', border: '3px dashed var(--primary)', padding: 6,
              background: 'var(--input-bg)', color: 'var(--primary)', cursor: 'pointer',
            }}
            title="Clique pra escolher a foto, cole com Ctrl+V ou arraste o arquivo pra ca"
          >
            {fotoMostrando ? (
              <img src={fotoMostrando} alt="" style={{ maxHeight: 122, maxWidth: '100%', objectFit: 'contain', borderRadius: 6 }} />
            ) : (
              <>
                <Camera size={34} />
                <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: 1 }}>FOTO</span>
                <span style={{ fontSize: 10, fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>clique aqui<br />ou cole (Ctrl+V)</span>
              </>
            )}
          </button>
          {fotoMostrando && (
            <button type="button" className="btn-secundario" title="Tirar a foto"
              style={{ position: 'absolute', top: 6, right: 6, padding: '4px 7px' }}
              onClick={() => onChange((o) => ({ ...o, fotoNova: null, fotoApagar: !!o.fotoAtual }))}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label className="muted" style={{ fontSize: 11, display: 'block' }}>Nome do produto</label>
              <input value={valor.semCadastro ?? ''} onChange={(e) => { const v = upperMantendoCursor(e); onChange((o) => ({ ...o, semCadastro: v })); }}
                maxLength={200} placeholder="Escreva o nome do produto..." autoFocus
                style={{ width: '100%', fontSize: 16, fontWeight: 700, padding: '12px 14px' }} />
              {/* O codigo e' do SISTEMA (pedido dele): sequencial VP#####, gerado pelo banco ao gravar —
                  como o NP do Novos Produtos. Aqui so se mostra, numa linha embaixo do nome, pra a
                  Marca ter a largura toda na coluna estreita (esboco aprovado em 31/10/2026). */}
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                Codigo: <b style={valor.codigoLivre ? { color: 'var(--input-fg)' } : undefined}>{valor.codigoLivre || 'gerado ao registrar'}</b>
              </div>
            </div>
            {comTrocar && <button type="button" className="btn-secundario" onClick={trocar}>Trocar</button>}
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label className="muted" style={{ fontSize: 11, display: 'block' }}>Marca (opcional)</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ComboBox opcoes={opcoesMarca} value={valor.marcaId}
                    onChange={(v) => onChange((o) => ({ ...o, marcaId: v === '' || v == null ? null : Number(v) }))}
                    placeholder="Escolha a marca..." />
                </div>
                <button type="button" className="btn-secundario" onClick={() => setNovaMarca(true)}
                  style={{ padding: '0 14px', whiteSpace: 'nowrap' }} title="Cadastrar uma marca nova">
                  <Plus size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
        {novaMarca && (
          <ModalCadastroRapido
            titulo="Nova Marca"
            endpoint="/api/marcas"
            campos={[{ key: 'nome', label: 'Nome', obrigatorio: true }]}
            onFechar={() => setNovaMarca(false)}
            onSalvo={(novo: { id: number; nome: string }) => {
              setMarcas((prev) => [...prev, novo].sort((a, b) => a.nome.localeCompare(b.nome)));
              onChange((o) => ({ ...o, marcaId: novo.id }));
              setNovaMarca(false);
            }}
          />
        )}
      </div>
    );
  }

  if (!valor.produto) {
    // "+ Novo Produto" (pedido dele): a porta do produto sem cadastro a vista, nao so na ultima linha
    // da lista (que continua la, com o mesmo nome, trazendo o texto digitado). Na linha de BAIXO da
    // busca, ao lado de "Nao achou na busca?" — a busca fica com a largura toda (esboco de 31/10/2026).
    return (
      <div>
        <BuscaProdutoEstoque endpoint="/api/vendas-perdidas/buscar" grande
          placeholder="Codigo ou nome..." rotuloSemCadastro="Novo Produto"
          onEscolher={(p) => { onChange((o) => ({ ...o, produto: p, semLugares: false })); onEscolheu?.(); }}
          onSemCadastro={(texto) => onChange((o) => ({ ...o, semCadastro: texto }))} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 10 }}>
          <span className="muted" style={{ fontSize: 14, fontWeight: 600 }}>Nao achou na busca?</span>
          <button type="button" className="btn-primario" onClick={() => onChange((o) => ({ ...o, semCadastro: '' }))}
            style={{ fontSize: 15, fontWeight: 700, padding: '10px 20px', whiteSpace: 'nowrap', flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Plus size={18} /> Novo Produto
          </button>
        </div>
      </div>
    );
  }

  const p = valor.produto;
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
      {p.foto_url ? <FotoProduto url={p.foto_url} tam={56} /> : <div className="produto-thumb-vazio" style={{ width: 56, height: 56, flex: '0 0 auto' }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <span className="text-gray-500 font-bold no-underline" style={{ fontSize: 12 }}>{p.codigo_interno}</span>{' '}
        <strong style={{ fontSize: 16 }}>{p.descricao}</strong>
        <div className="muted" style={{ fontSize: 11 }}>
          {p.tipo === 'novo_produto' ? 'Novos Produtos'
            : valor.semLugares ? 'Produto do cadastro'
            : p.lugares.map((l) => l.codigo).join(' · ') || 'sem prateleira'}
        </div>
      </div>
      {comTrocar && <button type="button" className="btn-secundario" onClick={trocar}>Trocar</button>}
    </div>
  );
}

function Passo({ n, titulo }: { n: number; titulo: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
      <span style={{
        width: 30, height: 30, borderRadius: '50%', background: 'var(--primary)', color: '#fff', flex: '0 0 auto',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
      }}>{n}</span>
      {titulo}
    </div>
  );
}

function faltaNoForm(o: Origem, quantidade: string): string | null {
  const qtdNum = Number(quantidade.replace(',', '.'));
  return !origemEscolhida(o) ? 'Escolha o produto.'
    : o.semCadastro !== null && !o.semCadastro.trim() ? 'Escreva o nome do produto.'
    : !quantidade.trim() ? 'Diga quantas unidades o cliente queria.'
    : !Number.isFinite(qtdNum) || qtdNum <= 0 ? 'A quantidade precisa ser maior que zero.'
    : null;
}

/** O registro como estava, montado de volta na forma do formulario. */
function origemDoItem(i: Item): Origem {
  if (i.tipo === 'sem_cadastro') {
    return {
      ...ORIGEM_VAZIA, semCadastro: i.descricao, fotoAtual: i.foto_url,
      codigoLivre: i.codigo_livre ?? '', marcaId: i.marca_id, marcaNome: i.marca,
    };
  }
  return {
    ...ORIGEM_VAZIA,
    semLugares: true,
    produto: {
      id: Number(i.tipo === 'novo_produto' ? i.novo_produto_id : i.produto_id),
      tipo: i.tipo === 'novo_produto' ? 'novo_produto' : 'produto',
      codigo_interno: i.codigo_interno, descricao: i.descricao, foto_url: i.foto_url,
      unidade: i.unidade, fora_da_auditoria: false, lugares: [],
    },
  };
}

const fmt = (v: string | number | null | undefined) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { maximumFractionDigits: 3 }) : String(v);
};

// "31/10 08:10" — sem ano e sem segundos pra coluna caber na lista da direita (esboco aprovado); o
// ano ja esta no filtro De/Ate, e o carimbo inteiro fica no `title` da celula.
function quandoCurto(iso: string): string {
  const s = formatDataHoraSP(iso);
  const m = /^(\d{2}\/\d{2})\/\d{4},?\s+(\d{2}:\d{2})/.exec(s);
  return m ? `${m[1]} ${m[2]}` : s;
}

function hojeISO(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}
function inicioDoMesISO(): string {
  return `${hojeISO().slice(0, 7)}-01`;
}

const CHAVE_VISTA = 'fdc.vendas-perdidas.vista';

function ModalEditar({ item, onFechar, onGravado }: { item: Item; onFechar: () => void; onGravado: () => void }) {
  const dialog = useDialog();
  const [origem, setOrigem] = useState<Origem>(() => origemDoItem(item));
  // Sem separador de milhar: o campo e' lido como o do registro (so a virgula vira ponto).
  const [quantidade, setQuantidade] = useState(() => String(Number(item.quantidade)).replace('.', ','));
  const [observacao, setObservacao] = useState(item.observacao ?? '');
  const [gravando, setGravando] = useState(false);
  const falta = faltaNoForm(origem, quantidade);

  async function salvar() {
    if (falta || gravando) return;
    setGravando(true);
    try {
      const r = await api.put<{ foto_falhou?: boolean }>(`/api/vendas-perdidas/${item.id}`, {
        ...payloadOrigem(origem),
        quantidade: Number(quantidade.replace(',', '.')),
        observacao: observacao.trim() || null,
      });
      if (r?.foto_falhou) await dialog.alerta('O registro foi salvo, mas a FOTO nao subiu.', { titulo: 'Foto nao gravou', tipo: 'aviso' });
      else dialog.toast('Venda perdida alterada');
      onGravado();
    } catch (e: any) {
      await dialog.alerta(e?.corpo?.detalhe ?? e?.message ?? 'Nao foi possivel salvar.', { titulo: 'Nao gravou', tipo: 'aviso' });
    } finally {
      setGravando(false);
    }
  }

  return (
    <Modal aberto onFechar={onFechar} titulo={`Editar venda perdida — ${formatDataHoraSP(item.criado_em)} · ${item.usuario ?? '?'}`} largura={760}
      rodape={(
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
          {falta && <span style={{ fontSize: 13, color: 'var(--num-destaque)', fontWeight: 600, marginRight: 'auto' }}>{falta}</span>}
          <button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>
          <button type="button" className="btn-primario" onClick={() => void salvar()} disabled={!!falta || gravando}>
            {gravando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <CampoOrigem valor={origem} onChange={setOrigem} colarAtivo comTrocar />
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label className="muted" style={{ fontSize: 11, display: 'block' }}>Quantidade</label>
            <input value={quantidade} onChange={(e) => setQuantidade(e.target.value)} inputMode="decimal"
              style={{ width: 120, textAlign: 'right', fontSize: 16, fontWeight: 700 }} />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label className="muted" style={{ fontSize: 11, display: 'block' }}>Observacao (opcional)</label>
            <input value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={300}
              placeholder="ex.: cliente levou do concorrente" style={{ width: '100%' }} />
          </div>
        </div>
        {origem.produto && origem.produto.tipo !== 'novo_produto' && (item.tipo !== 'produto' || origem.produto.id !== item.produto_id) && (
          <div className="muted" style={{ fontSize: 12 }}>
            Trocando o produto, o "Sistema dizia" passa a ser o estoque deste produto em {formatDataHoraSP(item.criado_em)}, tirado do historico de movimentacoes.
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function VendasPerdidas() {
  const dialog = useDialog();
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin';
  const podeEditar = ehAdmin || usuario?.perfil === 'gerente';

  const [origem, setOrigem] = useState<Origem>(ORIGEM_VAZIA);
  const [quantidade, setQuantidade] = useState('');
  const [observacao, setObservacao] = useState('');
  const [gravando, setGravando] = useState(false);
  const [editando, setEditando] = useState<Item | null>(null);
  const [versaoForm, setVersaoForm] = useState(0);
  const [fotoAmpliada, setFotoAmpliada] = useState<ImagemLightbox | null>(null);

  const [de, setDe] = useState(inicioDoMesISO);
  const [ate, setAte] = useState(hojeISO);
  const [vista, setVista] = useState<'registros' | 'produtos'>(() => {
    try { return localStorage.getItem(CHAVE_VISTA) === 'produtos' ? 'produtos' : 'registros'; } catch { return 'registros'; }
  });
  useEffect(() => { try { localStorage.setItem(CHAVE_VISTA, vista); } catch { /* preferencia, nao dado */ } }, [vista]);

  const [itens, setItens] = useState<Item[]>([]);
  const [resumo, setResumo] = useState<Resumo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const reqId = useRef(0);
  const campoQtd = useRef<HTMLInputElement>(null);

  const carregar = useCallback(async () => {
    const req = ++reqId.current;
    setCarregando(true);
    try {
      const r = await api.get<{ itens: Item[]; resumo: Resumo[] }>(`/api/vendas-perdidas?de=${de}&ate=${ate}`);
      if (req !== reqId.current) return;
      setItens(r.itens);
      setResumo(r.resumo);
    } catch { /* lista vazia ja diz que nao carregou */ } finally {
      if (req === reqId.current) setCarregando(false);
    }
  }, [de, ate]);

  useEffect(() => { void carregar(); }, [carregar]);

  const qtdNum = Number(quantidade.replace(',', '.'));
  const falta = faltaNoForm(origem, quantidade);

  async function registrar() {
    if (falta || gravando) return;
    setGravando(true);
    try {
      const { foto_apagar: _ignorado, ...corpo } = payloadOrigem(origem) as Record<string, unknown>;
      const r = await api.post<{ foto_falhou?: boolean }>('/api/vendas-perdidas', { ...corpo, quantidade: qtdNum, observacao: observacao.trim() || null });
      if (r?.foto_falhou) {
        await dialog.alerta('A venda perdida foi registrada, mas a FOTO nao subiu.', { titulo: 'Foto nao gravou', tipo: 'aviso' });
      } else {
        dialog.toast(`Venda perdida registrada: ${origem.produto ? origem.produto.codigo_interno ?? '' : (origem.semCadastro ?? '').trim()}`);
      }
      cancelar();
      reqId.current++;
      void carregar();
    } catch (e: any) {
      await dialog.alerta(e?.corpo?.detalhe ?? e?.message ?? 'Nao foi possivel registrar.', { titulo: 'Nao gravou', tipo: 'aviso' });
    } finally {
      setGravando(false);
    }
  }

  // Recomeca o formulario inteiro — inclusive o texto da busca, que mora DENTRO da busca (por isso a
  // `key`: remontar e' o unico jeito de zera-lo de fora).
  function cancelar() {
    setOrigem(ORIGEM_VAZIA);
    setQuantidade('');
    setObservacao('');
    setVersaoForm((v) => v + 1);
  }

  async function excluir(i: Item) {
    const nome = i.codigo_interno ?? i.descricao;
    const premio = i.tipo === 'produto' ? ' Ele sai da conta do Premio do Estoquista.' : '';
    const ok = await dialog.confirmar(`Excluir o registro de ${nome} (${fmt(i.quantidade)} ${i.unidade}) de ${i.usuario ?? '?'}?${premio}`, { titulo: 'Excluir venda perdida', perigoso: true });
    if (!ok) return;
    try {
      await api.delete(`/api/vendas-perdidas/${i.id}`);
      void carregar();
    } catch (e: any) {
      await dialog.alerta(e?.corpo?.detalhe ?? e?.message ?? 'Nao foi possivel excluir.', { titulo: 'Nao excluiu', tipo: 'aviso' });
    }
  }

  return (
    <div className="pagina" style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <PackageX size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Venda Perdida
        </h2>
      </div>

      {/* ── O REGISTRO ── layout do esboco aprovado em 31/10/2026 ("o perfil do usuario que vai
          preencher esta tela e extremamente baixo em conhecimento de informatica"): dois passos
          numerados, campos grandes, "+ Novo Produto" a vista e Cancelar ao lado do Registrar. */}
      {/* Cadastro a ESQUERDA e lista a DIREITA (pedido dele, esboco aprovado 31/10/2026: "os campos de
          cadastro e edicao nao devem precisar rolar"). A pagina ocupa a altura da area de conteudo e
          NAO rola; quem rola e' so a lista, por dentro. O cadastro tem `overflowY: auto` so como
          rede pra tela menor que a medida (1536x708 uteis, a dele). */}
      {/* O painel desce ate o fim (pedido dele: "vamos aproveitar este espaco", esboco aprovado) e o
          rodape com Cancelar/Registrar fica no pe dele (`marginTop: auto`). Fontes 2px menores que o
          esboco anterior, tambem a pedido. */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 16, alignItems: 'stretch' }}>
      <div style={{ border: '1px solid var(--panel-border)', borderRadius: 6, padding: 16, background: 'var(--panel-bg)', width: 560, flex: '0 0 auto', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>O cliente queria este produto e nao levou</div>
        <div>
          <Passo n={1} titulo={origem.semCadastro !== null ? 'Novo Produto' : 'Qual produto o cliente queria?'} />
          <div style={{ paddingLeft: 40 }}>
            <CampoOrigem key={versaoForm} valor={origem} onChange={setOrigem} colarAtivo={!editando}
              onEscolheu={() => setTimeout(() => campoQtd.current?.focus(), 0)} />
          </div>
        </div>
        <div>
          <Passo n={2} titulo={`Quantas unidades o cliente queria?${origem.produto ? ` (${origem.produto.unidade})` : ''}`} />
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', paddingLeft: 40 }}>
            {/* Sem `cel-num`: aquela classe tira o respiro global dos campos (feita pra celula de
                tabela) e o campo saia mais baixo que a Observacao ao lado. */}
            <input ref={campoQtd} value={quantidade} onChange={(e) => setQuantidade(e.target.value)} inputMode="decimal"
              aria-label="Quantidade" style={{ width: 130, textAlign: 'right', fontSize: 18, fontWeight: 700, padding: '12px 14px' }} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <label className="muted" style={{ fontSize: 11, display: 'block' }}>Observacao (opcional)</label>
              <input value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={300}
                placeholder="ex.: cliente levou do concorrente" style={{ width: '100%', fontSize: 14, padding: '12px 14px' }} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', alignItems: 'center', borderTop: '2px solid var(--page-bg)', paddingTop: 14, marginTop: 'auto' }}>
          {falta && <span style={{ marginRight: 'auto', fontSize: 13, fontWeight: 700, color: 'var(--num-destaque)' }}>{falta}</span>}
          {/* Cancelar = desistir do registro inteiro e voltar ao inicio (substitui o "Trocar"). */}
          <button type="button" className="btn-secundario" onClick={cancelar} disabled={gravando}
            style={{ fontSize: 15, fontWeight: 700, padding: '12px 26px', whiteSpace: 'nowrap' }}>
            Cancelar
          </button>
          <button type="button" className="btn-primario" onClick={() => void registrar()} disabled={!!falta || gravando}
            style={{ fontSize: 15, fontWeight: 700, padding: '12px 26px', whiteSpace: 'nowrap' }}>
            {gravando ? 'Registrando...' : 'Registrar venda perdida'}
          </button>
        </div>
      </div>

      {/* ── A LISTA ── coluna da direita: filtros fixos em cima, grid rolando por dentro. */}
      <div style={{ flex: 1, minWidth: 0, alignSelf: 'stretch', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <label className="muted" style={{ fontSize: 11, display: 'block' }}>De</label>
          <input type="date" value={de} onChange={(e) => setDe(e.target.value)} />
        </div>
        <div>
          <label className="muted" style={{ fontSize: 11, display: 'block' }}>Ate</label>
          <input type="date" value={ate} onChange={(e) => setAte(e.target.value)} />
        </div>
        <div style={{ display: 'inline-flex', border: '1px solid var(--panel-border)', borderRadius: 6, overflow: 'hidden' }}>
          {([['registros', 'Registros'], ['produtos', 'Por produto']] as const).map(([id, rot]) => (
            <button key={id} type="button" onClick={() => setVista(id)} aria-pressed={vista === id}
              style={{
                padding: '8px 14px', fontSize: 14, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                background: vista === id ? 'var(--primary)' : 'transparent', color: vista === id ? '#fff' : 'inherit',
                fontWeight: vista === id ? 700 : 400,
              }}>{rot}</button>
          ))}
        </div>
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          {carregando ? '' : vista === 'registros'
            ? (itens.length === 1 ? '1 registro' : `${itens.length} registros`)
            : (resumo.length === 1 ? '1 produto' : `${resumo.length} produtos`)}
        </span>
      </div>

      <div className="overflow-x-auto" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {vista === 'registros' ? (
          // Compacta (a da lista de Produtos): na coluna da direita a lateral de 12px estourava a
          // largura em 26px com a lista cheia — medido na tela dele, 1536px uteis.
          <table className="tabela tabela-compacta" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Codigo</th>
                <th style={{ textAlign: 'left' }}>Quando</th>
                <th style={{ textAlign: 'left' }}>Foto</th>
                <th style={{ textAlign: 'left' }}>Produto</th>
                <th style={{ textAlign: 'left' }}>Marca</th>
                <th style={{ textAlign: 'right' }}>Qtd</th>
                <th style={{ textAlign: 'right' }}>Sistema<br />dizia</th>
                <th style={{ textAlign: 'left' }}>Quem</th>
                <th style={{ textAlign: 'left' }}>Observacao</th>
                {podeEditar && <th />}
              </tr>
            </thead>
            <tbody>
              {itens.length === 0 && !carregando && (
                <tr><td colSpan={podeEditar ? 10 : 9} className="muted" style={{ padding: 12 }}>Nenhuma venda perdida no periodo.</td></tr>
              )}
              {itens.map((i) => (
                <tr key={i.id}>
                  <td style={{ whiteSpace: 'nowrap' }}><span className="text-gray-500 font-bold no-underline">{i.codigo_interno ?? '—'}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }} title={formatDataHoraSP(i.criado_em)}>{quandoCurto(i.criado_em)}</td>
                  <td style={{ width: 44 }}><CelulaFoto url={i.foto_url} legenda={`${i.codigo_interno ?? ''} ${i.descricao}`.trim()} onAmpliar={setFotoAmpliada} /></td>
                  <td>{i.descricao}<SeloOrigem tipo={i.tipo} /></td>
                  <td>{i.marca || '—'}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} className="cel-num">{fmt(i.quantidade)} {i.unidade}</td>
                  <td style={{ textAlign: 'right' }} className="cel-num">{fmt(i.estoque_no_registro)}</td>
                  <td>{i.usuario ?? '—'}</td>
                  <td>{i.observacao ?? ''}</td>
                  {podeEditar && (
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button type="button" title="Editar registro" onClick={() => setEditando(i)}
                        style={{ background: 'none', border: 0, cursor: 'pointer', color: 'inherit', marginRight: ehAdmin ? 10 : 0 }}>
                        <Pencil size={15} />
                      </button>
                      {ehAdmin && (
                        <button type="button" title="Excluir registro" onClick={() => void excluir(i)}
                          style={{ background: 'none', border: 0, cursor: 'pointer', color: 'inherit' }}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="tabela tabela-compacta" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Foto</th>
                <th style={{ textAlign: 'left' }}>Produto</th>
                <th style={{ textAlign: 'right' }}>Registros</th>
                <th style={{ textAlign: 'right' }}>Qtd perdida</th>
                <th style={{ textAlign: 'right' }}>Estoque hoje</th>
              </tr>
            </thead>
            <tbody>
              {resumo.length === 0 && !carregando && (
                <tr><td colSpan={5} className="muted" style={{ padding: 12 }}>Nenhuma venda perdida no periodo.</td></tr>
              )}
              {resumo.map((r) => (
                <tr key={r.chave}>
                  <td style={{ width: 44 }}><CelulaFoto url={r.foto_url} legenda={`${r.codigo_interno ?? ''} ${r.descricao}`.trim()} onAmpliar={setFotoAmpliada} /></td>
                  <td><span className="text-gray-500 font-bold no-underline">{r.codigo_interno}</span> {r.descricao}<SeloOrigem tipo={r.tipo} /></td>
                  <td style={{ textAlign: 'right' }} className="cel-num">{r.registros}</td>
                  <td style={{ textAlign: 'right' }} className="cel-num">{fmt(r.quantidade)} {r.unidade}</td>
                  <td style={{ textAlign: 'right' }} className="cel-num">{fmt(r.estoque_atual)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      </div>
      </div>

      {fotoAmpliada && <LightboxImagem imagens={[fotoAmpliada]} onFechar={() => setFotoAmpliada(null)} />}

      {editando && (
        <ModalEditar key={editando.id} item={editando} onFechar={() => setEditando(null)}
          onGravado={() => { setEditando(null); reqId.current++; void carregar(); }} />
      )}
    </div>
  );
}
